<template>
  <section class="guestsec">
    <header class="guestsec__head">
      <span class="guestsec__mark" />
      <h2 class="guestsec__title">Guest access</h2>
      <button
        class="guestsec__invite-btn"
        type="button"
        @click="dialogOpen = true"
      >
        <Icon name="plus" :size="14" />
        <span>Invite guest</span>
      </button>
      <p class="guestsec__sub">
        Externe onderzoekers (bijv. bij de klant) die via een e-maillink toegang
        hebben tot alléén dit project. Toegang verloopt automatisch; beheer en
        budgetten in Admin → Guests.
      </p>
    </header>

    <p v-if="loading" class="guestsec__hint">Loading guests…</p>
    <p v-else-if="!grants.length" class="guestsec__hint">
      Nog geen gasten uitgenodigd voor dit project.
    </p>

    <div v-else class="guestsec__list">
      <div v-for="g in grants" :key="g.id" class="guestsec__row">
        <div class="guestsec__row-text">
          <div class="guestsec__email">{{ g.email }}</div>
          <div class="guestsec__meta">
            until {{ formatDate(g.expiresAtMs) }}
            <template v-if="g.tokenBudget">
              · {{ formatTokens(g.tokensUsed) }} /
              {{ formatTokens(g.tokenBudget) }} tokens
            </template>
            <template v-if="g.invitedBy && g.invitedBy.email">
              · invited by {{ inviterLabel(g) }}
            </template>
          </div>
        </div>
        <span
          class="guestsec__status"
          :class="`guestsec__status--${displayStatus(g)}`"
          >{{ displayStatus(g) }}</span
        >
        <button
          v-if="['invited', 'active'].includes(displayStatus(g))"
          class="guestsec__copy"
          type="button"
          title="Copy the invite message to send to this guest"
          @click="copyInvite(g)"
        >
          {{ copiedId === g.id ? "Copied!" : "Copy invite" }}
        </button>
        <button
          v-if="displayStatus(g) !== 'revoked'"
          class="guestsec__copy guestsec__copy--danger"
          type="button"
          :disabled="revokingId === g.id"
          title="Revoke access permanently"
          @click="revoke(g)"
        >
          {{ revokingId === g.id ? "Revoking…" : "Revoke" }}
        </button>
      </div>
    </div>

    <p v-if="actionError" class="guestsec__error">{{ actionError }}</p>

    <GuestInviteDialog
      :dialog="dialogOpen"
      :project-id="projectId"
      :project-title="projectTitle"
      @close="dialogOpen = false"
      @created="load"
    />
  </section>
</template>

<script>
import { collection, getDocs, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/firebase";
import Icon from "@/components/alice/Icon.vue";
import GuestInviteDialog from "@/components/GuestInviteDialog.vue";

export default {
  name: "GuestAccessSection",
  components: { Icon, GuestInviteDialog },
  props: {
    projectId: { type: String, required: true },
    projectTitle: { type: String, default: "" },
  },
  data() {
    return {
      grants: [],
      loading: false,
      dialogOpen: false,
      copiedId: null,
      revokingId: null,
      actionError: null,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const snap = await getDocs(
          query(
            collection(db, "guestAccess"),
            where("projectId", "==", this.projectId)
          )
        );
        this.grants = snap.docs
          .map((d) => {
            const g = { id: d.id, ...d.data() };
            g.expiresAtMs =
              g.expiresAt && typeof g.expiresAt.toMillis === "function"
                ? g.expiresAt.toMillis()
                : Number(g.expiresAt) || null;
            return g;
          })
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      } catch (err) {
        console.error("GuestAccessSection load failed:", err);
        this.grants = [];
      } finally {
        this.loading = false;
      }
    },
    displayStatus(g) {
      if (
        ["invited", "active"].includes(g.status) &&
        g.expiresAtMs &&
        g.expiresAtMs <= Date.now()
      ) {
        return "expired";
      }
      return g.status;
    },
    inviterLabel(g) {
      const email = (g.invitedBy && g.invitedBy.email) || "";
      return email.split("@")[0] || "—";
    },
    formatDate(ms) {
      if (!ms) return "—";
      return new Date(ms).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    formatTokens(n) {
      const v = n || 0;
      if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
      if (v >= 1_000) return `${Math.round(v / 1_000)}k`;
      return String(v);
    },
    async revoke(g) {
      const title = this.projectTitle || "this project";
      if (
        !confirm(
          `Revoke ${g.email}'s access to "${title}"?\n\n` +
            `They lose access immediately. Re-inviting creates fresh access.`
        )
      ) {
        return;
      }
      this.revokingId = g.id;
      this.actionError = null;
      try {
        const revokeGuestGrant = httpsCallable(functions, "revokeGuestGrant");
        await revokeGuestGrant({ grantId: g.id });
        await this.load();
      } catch (err) {
        console.error("revokeGuestGrant failed:", err);
        this.actionError =
          err?.message?.replace(/^Firebase: /, "") ||
          "Couldn't revoke access. Please try again.";
      } finally {
        this.revokingId = null;
      }
    },
    async copyInvite(g) {
      const title = this.projectTitle || "our project";
      const text =
        `You've been invited to collaborate on "${title}" in Alice, ` +
        `Valsplat's research workspace.\n\n` +
        `1. Go to ${window.location.origin}/guest\n` +
        `2. Enter this email address (${g.email})\n` +
        `3. Open the sign-in link you receive — you're in.\n\n` +
        `Your access runs until ${this.formatDate(g.expiresAtMs)}.`;
      try {
        await navigator.clipboard.writeText(text);
        this.copiedId = g.id;
        setTimeout(() => (this.copiedId = null), 2000);
      } catch (err) {
        console.error("clipboard write failed:", err);
      }
    },
  },
};
</script>

<style scoped>
/* Mirrors the alice-det section styling so it reads as one page. */
.guestsec {
  border-bottom: 1px solid var(--border-hairline);
  padding: 28px 0 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.guestsec:last-child {
  border-bottom: none;
}
.guestsec__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}
.guestsec__mark {
  width: 12px;
  height: 12px;
  background: var(--vp-turquoise-100, #00a8a8);
  flex-shrink: 0;
}
.guestsec__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--vp-black);
}
.guestsec__sub {
  flex-basis: 100%;
  margin: 0 0 0 24px;
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-3);
  line-height: 1.5;
}
.guestsec__invite-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  margin-left: auto;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--vp-black);
  border-radius: 0;
  transition: background 0.12s ease, color 0.12s ease;
}
.guestsec__invite-btn:hover {
  background: var(--vp-black);
  color: var(--vp-white);
}
.guestsec__hint {
  margin: 0;
  font-family: var(--font-ui);
  font-size: 12px;
  font-style: italic;
  color: var(--fg-3);
}
.guestsec__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.guestsec__row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 14px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  max-width: 640px;
}
.guestsec__row-text {
  flex: 1;
  min-width: 0;
}
.guestsec__email {
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 600;
  color: var(--vp-black);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.guestsec__meta {
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.guestsec__status {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-3);
  white-space: nowrap;
}
.guestsec__status--active {
  color: var(--vp-turquoise-100, #00a8a8);
}
.guestsec__status--invited {
  color: var(--vp-geel-110, #b58a00);
}
.guestsec__status--disabled,
.guestsec__status--revoked,
.guestsec__status--expired {
  color: var(--vp-rood-100, #d43c3c);
}
.guestsec__copy {
  padding: 4px 10px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  border-radius: 0;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--vp-black);
  white-space: nowrap;
}
.guestsec__copy:hover {
  border-color: var(--vp-black);
}
.guestsec__copy--danger:hover {
  border-color: var(--vp-rood-100, #d43c3c);
  color: var(--vp-rood-100, #d43c3c);
}
.guestsec__copy:disabled {
  opacity: 0.6;
  cursor: default;
}
.guestsec__error {
  margin: 0;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--vp-rood-100, #d43c3c);
}
</style>
